"use client";

import { useNoteLabels, useOtherNoteLabel } from "@/hooks/use-notation";
import {
  pitchClassToName,
  ROOT_PITCH_CLASS,
} from "@/lib/music/theory-helpers";
import {
  BORDER,
  FONT_MONO,
  SURFACE,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "@/components/ui/organisms/chord-practice/palette";
import { LEXICON, Term } from "./harmony-term";

interface ScaleFriezeProps {
  /** Tonique en notation anglaise (C, D, Eb, F#, …). */
  tonic: string;
  /**
   * Demi-tons depuis la tonique pour les 7 degrés de la gamme.
   * On n&apos;affiche pas la reprise de la tonique à l&apos;octave , 
   * la frise montre les 7 degrés distincts. Le ½ ton final
   * (sensible → tonique) reste visible via `FormulaExplanation`
   * en dessous, qui calcule la liste complète des demi-tons.
   */
  semitones: number[];
  /** Influence le narratif sous la frise. */
  flavor: "major" | "minor";
  /**
   * Compact = pastilles plus petites (overview).
   * Generous = pastilles plus larges (vue détail).
   */
  density?: "compact" | "generous";
  /**
   * Pitch-classes (0..11) actuellement pressées au piano.
   * Quand renseigné, les degrés correspondants s&apos;allument en live.
   * Optionnel pour préserver la rétro-compat des call sites statiques.
   */
  activePitchClasses?: ReadonlySet<number>;
}

/**
 * Frise pédagogique d&apos;intervalles : les degrés alignés avec les sauts
 * T/½ intercalés. Représentation de référence pour «&nbsp;la gamme&nbsp;»
 * à travers l&apos;app (overview + détail).
 */
export function ScaleFrieze({
  tonic,
  semitones,
  flavor,
  density = "compact",
  activePitchClasses,
}: ScaleFriezeProps) {
  const noteLabels = useNoteLabels();
  const otherNoteLabel = useOtherNoteLabel();
  const tonicPc = ROOT_PITCH_CLASS[tonic] ?? 0;
  // 7 degrés visibles dans la frise (pas la reprise tonique à l'octave).
  const pcs = semitones.map((iv) => (tonicPc + iv) % 12);
  const spellings = pcs.map((pc) => pitchClassToName(pc, tonic));
  const notes = noteLabels(spellings);
  const notesAlt = spellings.map(otherNoteLabel);

  // Intervalles affichés *entre* les pastilles : 6 chips pour 7 pastilles.
  const visualSteps: number[] = [];
  for (let i = 0; i < semitones.length - 1; i++) {
    visualSteps.push((semitones[i + 1] ?? 0) - (semitones[i] ?? 0));
  }

  // Pattern complet (7 sauts), utilisé par `FormulaExplanation` pour
  // identifier *tous* les demi-tons, y compris le 7→8 (sensible →
  // tonique) qui n'est plus représenté visuellement dans la frise.
  const lastSemi = semitones[semitones.length - 1] ?? 0;
  const fullSteps: number[] = [...visualSteps, 12 - lastSemi];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        width: "100%",
        maxWidth: 960,
      }}
    >
      <PatternIntro flavor={flavor} />
      <Frieze
        notes={notes}
        notesAlt={notesAlt}
        pcs={pcs}
        steps={visualSteps}
        density={density}
        activePitchClasses={activePitchClasses}
      />
      <FormulaExplanation flavor={flavor} steps={fullSteps} />
    </div>
  );
}

function PatternIntro({ flavor }: { flavor: "major" | "minor" }) {
  return (
    <p
      style={{
        margin: 0,
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
        color: TEXT_DIM,
        lineHeight: 1.55,
        maxWidth: 720,
      }}
    >
      {flavor === "major" ? (
        <>
          Toutes les gammes majeures suivent <em>exactement</em> le même{" "}
          <Term tip={LEXICON.pattern}>pattern d&apos;intervalles</Term>{" "}
          <strong style={{ color: TEXT }}>T·T·½·T·T·T·½</strong>. Quelle que
          soit la <Term tip={LEXICON.tonic}>tonique</Term>, c&apos;est ce
          squelette qui leur donne leur caractère lumineux commun.
        </>
      ) : (
        <>
          Les gammes mineures partagent un autre{" "}
          <Term tip={LEXICON.pattern}>pattern d&apos;intervalles</Term>, qui
          déplace les <Term tip={LEXICON.semitone}>demi-tons</Term> et donne
          la couleur sombre du mineur, quelle que soit la{" "}
          <Term tip={LEXICON.tonic}>tonique</Term>.
        </>
      )}
    </p>
  );
}

interface FriezeProps {
  notes: string[];
  notesAlt: string[];
  pcs: number[];
  steps: number[];
  density: "compact" | "generous";
  activePitchClasses?: ReadonlySet<number>;
}

function Frieze({
  notes,
  notesAlt,
  pcs,
  steps,
  density,
  activePitchClasses,
}: FriezeProps) {
  // Children are flattened (pill, chip, pill, chip, …, pill) so that
  // pills can `flex: 1` and share the available width equally. Chips
  // stay fixed-width, no fluid sizing needed for the T/½ glyphs.
  const items: React.ReactNode[] = [];

  notes.forEach((note, i) => {
    const pc = pcs[i] ?? 0;
    const pressed = activePitchClasses?.has(pc) ?? false;

    items.push(
      <DegreePill
        key={`pill-${i}`}
        degree={i + 1}
        note={note}
        noteAlt={notesAlt[i] ?? note}
        isTonic={i === 0}
        pressed={pressed}
        density={density}
      />,
    );

    if (i < steps.length) {
      items.push(
        <IntervalChip
          key={`chip-${i}`}
          semitones={steps[i] ?? 2}
          density={density}
        />,
      );
    }
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: density === "generous" ? 6 : 4,
        width: "100%",
        paddingBottom: 4,
      }}
    >
      {items}
    </div>
  );
}

function DegreePill({
  degree,
  note,
  noteAlt,
  isTonic,
  pressed,
  density,
}: {
  degree: number;
  note: string;
  noteAlt: string;
  isTonic: boolean;
  pressed: boolean;
  density: "compact" | "generous";
}) {
  const generous = density === "generous";
  const borderColor = pressed ? VIOLET_BRIGHT : isTonic ? VIOLET : BORDER;
  const background = pressed
    ? `linear-gradient(180deg, rgba(196,181,253,0.28), rgba(139,92,246,0.18))`
    : isTonic
      ? `linear-gradient(180deg, ${VIOLET_GLOW}, ${VIOLET_SOFT})`
      : SURFACE;
  const labelColor = pressed || isTonic ? VIOLET_BRIGHT : TEXT_MUTE;
  const noteColor = pressed || isTonic ? "#fff" : TEXT;

  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 0,
        padding: generous ? "14px 8px" : "10px 6px",
        borderRadius: generous ? 12 : 10,
        border: `1px solid ${borderColor}`,
        background,
        textAlign: "center",
        boxShadow: pressed ? "0 0 14px rgba(196,181,253,0.45)" : "none",
        transition:
          "border-color 120ms, background 120ms, box-shadow 120ms",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: generous ? 10 : 9,
          fontFamily: FONT_MONO,
          color: labelColor,
          letterSpacing: "0.12em",
          marginBottom: 4,
        }}
      >
        {degree}
      </div>
      <div
        style={{
          fontSize: generous ? 22 : 18,
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          color: noteColor,
          letterSpacing: "-0.01em",
        }}
      >
        {note}
      </div>
      <div
        style={{
          fontSize: generous ? 11 : 10,
          color: TEXT_MUTE,
          fontFamily: FONT_MONO,
          marginTop: 2,
        }}
      >
        {noteAlt}
      </div>
    </div>
  );
}

function IntervalChip({
  semitones,
  density,
}: {
  semitones: number;
  density: "compact" | "generous";
}) {
  const isHalf = semitones === 1;
  const label = isHalf ? "½" : semitones === 2 ? "T" : `${semitones / 2}T`;
  const generous = density === "generous";

  return (
    <div
      style={{
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minWidth: generous ? 32 : 26,
        height: generous ? 42 : 36,
        alignSelf: "center",
        borderRadius: 999,
        padding: generous ? "0 10px" : "0 8px",
        background: isHalf
          ? "rgba(139,92,246,0.22)"
          : "rgba(255,255,255,0.04)",
        border: `1px solid ${isHalf ? VIOLET_GLOW : BORDER}`,
        color: isHalf ? VIOLET_BRIGHT : TEXT_DIM,
        fontFamily: FONT_MONO,
        fontSize: generous ? 13 : 12,
        fontWeight: 600,
        letterSpacing: "0.04em",
        boxShadow: isHalf ? "0 0 8px rgba(139,92,246,0.25)" : "none",
      }}
      title={isHalf ? "demi-ton" : "ton"}
    >
      {label}
    </div>
  );
}

function FormulaExplanation({
  flavor,
  steps,
}: {
  flavor: "major" | "minor";
  steps: number[];
}) {
  const halfPositions = steps
    .map((s, i) => (s === 1 ? `${i + 1}→${i + 2}` : null))
    .filter((s): s is string => s !== null);

  return (
    <div
      style={{
        padding: "14px 16px",
        borderRadius: 10,
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 18,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <LegendBadge symbol="T" label="ton, 2 demi-tons" />
        <LegendBadge
          symbol="½"
          label="demi-ton, notes voisines au piano"
          highlight
        />
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            color: TEXT_MUTE,
            letterSpacing: "0.08em",
          }}
        >
          DEMI-TONS · {halfPositions.join(" · ") || ", "}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          color: TEXT_DIM,
          lineHeight: 1.55,
        }}
      >
        {flavor === "major" ? (
          <>
            Le caractère lumineux du majeur naît des{" "}
            <Term tip={LEXICON.semitone}>demi-tons</Term> placés entre les{" "}
            <Term tip={LEXICON.degree}>degrés</Term> 3↔4 et 7↔8, la{" "}
            <Term tip={LEXICON.sensible}>sensible</Term> (7) appelle la{" "}
            <Term tip={LEXICON.tonic}>tonique</Term>.
          </>
        ) : (
          <>
            Le mineur place ses{" "}
            <Term tip={LEXICON.semitone}>demi-tons</Term> différemment : la{" "}
            <Term tip={LEXICON.third}>tierce mineure</Term> (
            <Term tip={LEXICON.degree}>degré</Term> 3) donne sa couleur
            sombre à la gamme.
          </>
        )}
      </p>
    </div>
  );
}

function LegendBadge({
  symbol,
  label,
  highlight,
}: {
  symbol: string;
  label: string;
  highlight?: boolean;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontFamily: "Inter, sans-serif",
        fontSize: 11,
        color: TEXT_DIM,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: 999,
          fontFamily: FONT_MONO,
          fontSize: 11,
          fontWeight: 600,
          background: highlight
            ? "rgba(139,92,246,0.22)"
            : "rgba(255,255,255,0.04)",
          border: `1px solid ${highlight ? VIOLET_GLOW : BORDER}`,
          color: highlight ? VIOLET_BRIGHT : TEXT,
        }}
      >
        {symbol}
      </span>
      <span>{label}</span>
    </span>
  );
}
